import { clientConfig } from '@/config/clientConfig';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { useCan } from '@skewb/access-control/react';
import { Briefcase, Calendar, Edit, FileText, Laptop, Mail, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { AddMobileAccessDialog } from '../components/dialogs/AddMobileAccessDialog';
import { EditJobInformationDialog } from '../components/dialogs/EditJobInformationDialog';
import { useMobileAccess } from '../hooks/useUsers';
import type { ColorScheme, User, UserDetail } from '../types/user';
import { getThemeColors } from '../utils/themeColors';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface JobInformationSectionProps {
  user: User;
  colorScheme: ColorScheme;
  userId?: string;
  userDetail?: UserDetail;
}

export function JobInformationSection({
  user,
  colorScheme,
  userId,
  userDetail,
}: JobInformationSectionProps) {
  const colors = getThemeColors(colorScheme);
  const [editOpen, setEditOpen] = useState(false);
  const [mobileAccessOpen, setMobileAccessOpen] = useState(false);
  // Editing hits `update User` on the backend — require the ability so the actor never
  // sees an Edit affordance that would 403 (WRK-5474). `useCan` must be called
  // unconditionally (Rules of Hooks), so combine with the userId check afterwards.
  const canUpdateUser = useCan('update', 'User');
  const canEdit = !!userId && canUpdateUser;

  // Live mobile entitlements (which apps, mobile number, cognito status).
  const { data: mobileAccess, isLoading: mobileAccessLoading } = useMobileAccess(userId ?? '');
  const isMobileEnabled = mobileAccess?.isMobileUser ?? user.isMobileUser ?? false;
  const isVerified = mobileAccess?.verified ?? userDetail?.verified ?? false;
  const hasCognitoAccount = mobileAccess?.hasCognitoAccount ?? Boolean(userDetail?.cognitoUsername);
  const mobileApplications = mobileAccess?.applications ?? [];

  // SC-6617: brands that keep skills/disciplines in their own HR system don't
  // surface them on the profile. Both grids below size to what's left so hiding
  // a field doesn't leave a hole in the row.
  const showSkills = clientConfig.features.skills;
  const showDisciplines = clientConfig.features.disciplines;
  const statTileCols = showDisciplines ? 'md:grid-cols-3' : 'md:grid-cols-2';
  // 8 fields normally; dropping both leaves 6, which sits as two clean rows of
  // three. Dropping only one leaves 7, where four columns still reads better.
  const jobFieldCols = !showSkills && !showDisciplines ? 'md:grid-cols-3' : 'md:grid-cols-4';

  return (
    <div className="max-w-6xl space-y-6">
      <div className={`grid grid-cols-1 ${statTileCols} gap-4`}>
        <Card className={`p-4 bg-gradient-to-br ${colors.stat1} border border-border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${colors.stat1Text} opacity-70 mb-1`}>
                Employee ID
              </p>
              <p className={`text-xl font-bold ${colors.stat1Text}`}>
                {userDetail?.employeeId || 'N/A'}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg ${colors.stat1Icon} flex items-center justify-center`}
            >
              <Briefcase className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className={`p-4 bg-gradient-to-br ${colors.stat2} border border-border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${colors.stat2Text} opacity-70 mb-1`}>
                Start Date
              </p>
              <p className={`text-xl font-bold ${colors.stat2Text}`}>
                {formatDate(userDetail?.startDate)}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg ${colors.stat2Icon} flex items-center justify-center`}
            >
              <Calendar className="w-6 h-6" />
            </div>
          </div>
        </Card>

        {showDisciplines && (
          <Card className={`p-4 bg-gradient-to-br ${colors.stat3} border border-border`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium ${colors.stat3Text} opacity-70 mb-1`}>
                  Discipline
                </p>
                <p className={`text-xl font-bold ${colors.stat3Text}`}>
                  {userDetail?.discipline || user.team || 'N/A'}
                </p>
              </div>
              <div
                className={`w-12 h-12 rounded-lg ${colors.stat3Icon} flex items-center justify-center`}
              >
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </Card>
        )}
      </div>

      <CollapsibleCard
        title="Job Information"
        subtitle="Employment and role details"
        icon={Briefcase}
        iconBg="bg-gray-100 dark:bg-gray-900/30"
        iconColor="text-gray-600 dark:text-gray-400"
        actions={
          canEdit ? (
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={() => setEditOpen(true)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : null
        }
      >
        <div className={`grid grid-cols-1 ${jobFieldCols} gap-6`}>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Job Title
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.jobTitle || user.role || 'N/A'}
            </div>
          </div>
          {showDisciplines && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Discipline
              </label>
              <div className="text-sm text-foreground font-medium">
                {userDetail?.discipline || user.team || 'N/A'}
              </div>
            </div>
          )}
          {showSkills && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Skills
              </label>
              <div className="flex flex-wrap gap-1">
                {userDetail?.skills && userDetail.skills.length > 0 ? (
                  userDetail.skills.map((skill: { id: number; name: string }) => (
                    <Badge
                      key={skill.id}
                      className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                    >
                      {skill.name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground italic">No skills assigned</span>
                )}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Line Manager
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.lineManager || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Team Leader
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.teamLeader || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Teams
            </label>
            <div className="flex flex-wrap gap-1">
              {userDetail?.teams && userDetail.teams.length > 0 ? (
                userDetail.teams.map((t: { id: number; name: string }) => (
                  <Badge
                    key={t.id}
                    className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                  >
                    {t.name}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground italic">No teams assigned</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Start Date
            </label>
            <div className="text-sm text-foreground font-medium">
              {formatDate(userDetail?.startDate)}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Employment Type
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.employmentType || 'N/A'}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Work Contact Information"
        subtitle="Professional contact details"
        icon={Mail}
        iconBg="bg-green-100 dark:bg-green-900/30"
        iconColor="text-green-600 dark:text-green-400"
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Employee ID
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.employeeId || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Work Phone
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.workPhone || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Work Email
            </label>
            <div className="text-sm text-foreground font-medium break-all">
              {userDetail?.workEmail || 'N/A'}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Mobile Access"
        subtitle="Mobile application access status"
        icon={Laptop}
        iconBg="bg-purple-100 dark:bg-purple-900/30"
        iconColor="text-purple-600 dark:text-purple-400"
        defaultOpen={false}
        actions={
          canEdit && !isMobileEnabled && !mobileAccessLoading ? (
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={!hasCognitoAccount}
              title={hasCognitoAccount ? undefined : 'User has no Cognito account'}
              onClick={() => setMobileAccessOpen(true)}
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Add Mobile Access
            </Button>
          ) : undefined
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                User Verified
              </label>
              <Badge
                variant="outline"
                className={
                  isVerified
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                {isVerified ? 'Verified' : 'Not Verified'}
              </Badge>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Mobile Status
              </label>
              <Badge
                variant="outline"
                className={
                  isMobileEnabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                }
              >
                {mobileAccessLoading ? '…' : isMobileEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Mobile Number
              </label>
              <div className="text-sm text-foreground font-medium">
                {mobileAccess?.mobileNumber || 'N/A'}
              </div>
            </div>
          </div>

          {mobileApplications.length > 0 && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Application Entitlements
              </label>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left">
                      <th className="px-3 py-2 font-semibold text-muted-foreground">Application</th>
                      <th className="px-3 py-2 font-semibold text-muted-foreground">Client</th>
                      <th className="px-3 py-2 font-semibold text-muted-foreground">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mobileApplications.map((app) => (
                      <tr
                        key={app.userClientAppRoleId}
                        className="border-b border-border last:border-0"
                      >
                        <td className="px-3 py-2 text-foreground font-medium">
                          {app.appName || 'Unknown'}
                        </td>
                        <td className="px-3 py-2 text-foreground">{app.clientName || 'N/A'}</td>
                        <td className="px-3 py-2 text-foreground">{app.roleName || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!isMobileEnabled && !mobileAccessLoading && !hasCognitoAccount && (
            <p className="text-xs text-muted-foreground italic">
              Mobile access can’t be added until this user has a Cognito account. Create the account
              (or set a work email) first.
            </p>
          )}
        </div>
      </CollapsibleCard>

      {canEdit && userId && (
        <>
          <EditJobInformationDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            userId={userId}
            user={userDetail}
          />
          <AddMobileAccessDialog
            open={mobileAccessOpen}
            onOpenChange={setMobileAccessOpen}
            userId={userId}
            userName={user.name}
            defaultMobileNumber={mobileAccess?.mobileNumber ?? user.phone}
          />
        </>
      )}
    </div>
  );
}
